import React from "react";
import { Link } from "react-router-dom";
import InsightCard from "./insight-card";
import { formatInsightDate } from "./insight-utils";

const renderRelatedLink = (href) => {
  if (!href) return null;
  const segment = href.split("/").filter(Boolean).pop() || href;
  const label = segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Link key={href} to={href} className="insight-pill-link">
      {label}
    </Link>
  );
};

const renderSectionContent = (section, index) => (
  <section key={`${section.heading}-${index}`} className="insight-article-section">
    {section.heading ? <h2>{section.heading}</h2> : null}
    {section.paragraphs?.map((paragraph, idx) => (
      <p key={`${section.heading}-p-${idx}`}>{paragraph}</p>
    ))}
    {section.bullets?.length ? (
      <ul>
        {section.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    ) : null}
  </section>
);

const InsightArticle = ({ post, relatedInsights = [], preview = false }) => {
  if (!post) return null;

  return (
    <article className={`insight-article${preview ? " insight-article--preview" : ""}`}>
      <header className="insight-article-hero">
        <div className="insight-article-meta-row">
          <span className="insight-article-category">{post.category}</span>
          <span>{post.readingTime}</span>
        </div>
        <h1>{post.title}</h1>
        {post.subtitle ? <p className="insight-article-subtitle">{post.subtitle}</p> : null}
        <div className="insight-article-meta">
          <span>{formatInsightDate(post.publishDate)}</span>
          {post.updatedDate ? <span>Updated {formatInsightDate(post.updatedDate)}</span> : null}
          <span>{post.authorName}</span>
          {post.authorTitle ? <span>{post.authorTitle}</span> : null}
        </div>
        {post.tags?.length ? (
          <div className="insight-chip-list" aria-label="Insight tags">
            {post.tags.map((tag) => (
              <span key={tag} className="insight-chip">
                {tag}
              </span>
            ))}
          </div>
        ) : null}
      </header>

      {post.heroImage ? (
        <div className="insight-hero-image">
          <img src={post.heroImage} alt={post.heroImageAlt || post.title} />
        </div>
      ) : null}

      <div className="insight-article-body">
        {post.excerpt ? <p className="insight-article-excerpt">{post.excerpt}</p> : null}
        {post.bodySections?.map(renderSectionContent)}

        {(post.relatedServices?.length || post.relatedIndustries?.length || post.relatedProducts?.length) ? (
          <section className="insight-article-section">
            <h2>Related resources</h2>
            <div className="insight-related-pills">
              {post.relatedServices?.map(renderRelatedLink)}
              {post.relatedIndustries?.map(renderRelatedLink)}
              {post.relatedProducts?.map(renderRelatedLink)}
            </div>
          </section>
        ) : null}

        {post.faqSection?.items?.length ? (
          <section className="insight-article-section">
            <h2>{post.faqSection.title || "Frequently asked questions"}</h2>
            <div className="insight-faq-list">
              {post.faqSection.items.map((item) => (
                <details key={item.question}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        ) : null}

        {relatedInsights.length ? (
          <section className="insight-article-section">
            <h2>Related insights</h2>
            <div className="row">
              {relatedInsights.map((item) => (
                <div key={item.slug} className="col-md-4 mb-4">
                  <InsightCard post={item} />
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {preview ? null : (
          <section className="insight-cta-band">
            <div>
              <p className="insight-cta-eyebrow">Discuss your roadmap</p>
              <h2>{post.ctaTitle}</h2>
              <p>{post.ctaText}</p>
            </div>
            <Link to={post.ctaButtonUrl || "/contact-us"} className="hero-v2-btn-primary">
              {post.ctaButtonText || "Contact Us"}
            </Link>
          </section>
        )}
      </div>
    </article>
  );
};

export default InsightArticle;
